/**
 * Sliding side panel styles for Creative Brief detail view.
 * Panel slides in from the right over the page content.
 * Two-column layout: 847px wide, image left (484px), cards right (290px).
 *
 * Ported from proxima.web — token imports replaced with inline values.
 */

import styled from '@emotion/styled';

const PANEL_WIDTH = 847;
const PANEL_Z_INDEX = 100;

/** Full-screen semi-transparent backdrop; fades in when panel opens */
export const PanelOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${PANEL_Z_INDEX};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  transition: opacity 0.3s ease;
`;

/** Panel container: slides in from the right */
export const PanelContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: ${PANEL_WIDTH}px;
  height: 100vh;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: ${PANEL_Z_INDEX + 1};
  display: flex;
  flex-direction: column;
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
  transition: transform 0.3s ease;
`;

/** Header: stacked — title row above, tags row below */
export const PanelHeader = styled.div`
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border-bottom: 1px solid #deeee6;
  flex-shrink: 0;
  display: flex;
`;

/** Single row inside the panel header: title left, actions right */
export const PanelHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/** Container for header action buttons (download + close) */
export const PanelActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/** Panel title text */
export const PanelTitle = styled.span`
  font-family: 'Figtree', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #192121;
`;

/** Close (×) button in panel header */
export const PanelCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 2px;
  background: none;
  color: #9ca3af;
  font-size: 20px;
  font-family: 'Figtree', sans-serif;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: #f9fafb;
    color: #192121;
  }
`;

/** Scrollable panel body */
export const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

/** Placeholder region for empty state or loading text */
export const TopPanelArea = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #9ca3af;
  font-family: 'Figtree', sans-serif;
  font-size: 13px;
`;

/** Row of tag chips beneath the panel title */
export const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/** Individual tag chip (format, aspect ratio, strategy, product) */
export const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: #deeee6;
  border-radius: 108px;
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  color: #131713;
  letter-spacing: 0.04em;
`;

/** Two-column design area: image left, cards right */
export const DesignArea = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  padding: 24px;
  background: #f9f9f9;
  flex: 1;
  min-height: 0;
`;

/** Left column: asset preview + storyboard strip */
export const ImageColumn = styled.div`
  width: 484px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

/** Right column: scrollable stack of brief cards */
export const RightColumn = styled.div`
  width: 290px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
`;

/** Card container used in the right column */
export const BriefCard = styled.div`
  background: #fff;
  border: 1px solid #deeee6;
  border-radius: 7px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
`;

/** Card header area — title + inline content */
export const CardHeader = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/** Card heading (e.g. "Brief Summary", "Audience Insights") */
export const CardTitle = styled.h3`
  margin: 0;
  font-family: 'Figtree', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #131713;
  letter-spacing: -0.03em;
  line-height: 1.3;
`;

/** Full-width horizontal rule divider between card sections */
export const CardDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid #deeee6;
  flex-shrink: 0;
`;

/** Padded section inside a card */
export const CardSection = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** Section label — regular weight */
export const CardSectionLabel = styled.span`
  display: block;
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #131713;
  line-height: 1.6;
`;

/** Section label — bold weight variant */
export const CardSectionLabelBold = styled.span`
  display: block;
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #131713;
  line-height: 1.6;
`;

/** Section body text */
export const CardSectionText = styled.p`
  margin: 0;
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #131713;
  line-height: 1.6;
`;

/** Section body text with optional line-clamp */
export const CardSectionTextClamped = styled(CardSectionText)<{ clamped: boolean }>`
  ${({ clamped }) =>
    clamped &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

/** "Show more / Show less" button below the concept description */
export const ShowMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  margin: 0 16px 12px;
  padding: 12px 24px;
  border: 1px solid #deeee6;
  border-radius: 10px;
  background: none;
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #131713;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f5f5f5;
  }
`;

/** Grid of audience key-value rows */
export const AudienceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/** Single audience key-value row */
export const AudienceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

/** Audience row key (label) */
export const AudienceKey = styled.span`
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #131713;
  min-width: 83px;
  line-height: 1.6;
`;

/** Audience row value */
export const AudienceValue = styled.span`
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #131713;
  line-height: 1.6;
`;

/** Full-panel loading/empty area */
export const LoadingArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  padding: 40px;
`;

/** Loading text displayed inside LoadingArea */
export const LoadingText = styled.p`
  margin: 0;
  font-family: 'Figtree', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #131713;
  text-align: center;
  letter-spacing: -0.03em;
`;

// ─── Asset Preview ──────────────────────────────────────────────────────────

/** Wrapper for the asset preview image area */
export const AssetPreviewWrapper = styled.div`
  position: relative;
  width: 100%;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  overflow: hidden;
  flex-shrink: 0;
`;

/** Displayed asset image */
export const AssetImage = styled.img`
  width: 100%;
  display: block;
  object-fit: contain;
  max-height: 600px;
`;

/** Small overlay mode indicator label (bottom-left) */
export const OverlayIndicator = styled.span`
  position: absolute;
  bottom: 8px;
  left: 8px;
  padding: 3px 8px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-family: 'Figtree', sans-serif;
  font-size: 11px;
  font-weight: 500;
  border-radius: 4px;
  pointer-events: none;
`;

/** Download button wrapper — inline in header */
export const DownloadButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

/** Download button — text style for the panel header */
export const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #131713;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.7;
  }
`;

/** Dropdown menu for download variants */
export const DownloadMenu = styled.div`
  position: absolute;
  top: 36px;
  right: 0;
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

/** Single item in the download dropdown */
export const DownloadMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: none;
  font-family: 'Figtree', sans-serif;
  font-size: 13px;
  color: #192121;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: #f9fafb;
  }

  & + & {
    border-top: 1px solid #e5e7eb;
  }
`;

// ─── Accordion ──────────────────────────────────────────────────────────────

/** Accordion section wrapper */
export const AccordionSection = styled.div`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

/** Accordion toggle button */
export const AccordionToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 24px;
  border: none;
  background: none;
  font-family: 'Figtree', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #192121;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: #f9fafb;
  }
`;

/** Accordion chevron indicator (rotates when collapsed) */
export const AccordionChevron = styled.span<{ collapsed: boolean }>`
  font-size: 12px;
  color: #9ca3af;
  transition: transform 0.2s ease;
  transform: rotate(${({ collapsed }) => (collapsed ? '-90deg' : '0deg')});
`;

/** Accordion collapsible body */
export const AccordionBody = styled.div<{ collapsed: boolean }>`
  display: ${({ collapsed }) => (collapsed ? 'none' : 'block')};
  padding: 0 24px 16px;
`;

/** Single copy variation item row */
export const VariationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

/** Variation text content */
export const VariationText = styled.span`
  flex: 1;
  font-family: 'Figtree', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
`;

/** Copy icon button */
export const CopyIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: none;
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  transition:
    background 0.1s,
    color 0.1s,
    border-color 0.1s;

  &:hover {
    background: #f9fafb;
    color: #192121;
    border-color: #d1d5db;
  }
`;
